import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form, getIn } from 'formik';
import lodashKeyBy from 'lodash/fp/keyBy';

import isBoolean from 'common/util/isBoolean';
import setNullEmptyValues from 'common/util/setNullEmptyValues';

import {
  SR_API_ACTION_PROPERTY_ATTRIBUTE_UPSERT_LIST,
  SR_API_ACTION_PROPERTY_ATTRIBUTE_DELETE_LIST,
} from 'api/actionTypes';

import {
  PROPERTY_ATTRIBUTE_CATEGORY_LISTING_FIELD,
  PROPERTY_ATTRIBUTE_CASCADING_BEHAVIOR_OPTION_ITEMS,
} from 'common/propertyAttributeConstants';

import getListingFormFields, {
  listingFormTypeToFieldgroupMapping,
} from 'common/listingFormFields';

import {
  createInitialValues,
  createFormValidationFn,
  scrollToFormField,
} from 'client/helpers/formikHelpers';

import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrFlex from 'client/components/SrFlex';
import ButtonDeprecated from 'client/components/ButtonDeprecated';
import SrFormikField, {
  SR_FIELD_THEME_ADMIN_HORIZONTAL_SMALL,
  SR_FIELD_THEME_CHECKBOX_BLUE,
} from 'client/components/SrFormikField';

import postWithState from 'client/actions/postWithState';
import withApiState from 'client/helpers/withApiState';

function getFormFields({ formType, formValues = {} }) {
  const listingFormFields = getListingFormFields({
    formType,
  });

  return listingFormFields.reduce((memo, listingFormField) => {
    const formFieldEnabled = getIn(formValues, `${listingFormField.id}.enabled`);
    const inputValidations = listingFormField.validations;
    const checkboxProperties = {};
    const petsAllowed = formValues.petsAllowed || {};
    const catsAllowed = formValues.catsAllowed || {};
    const dogsAllowed = formValues.dogsAllowed || {};

    if (
      listingFormField.id === 'maximumPetsAllowed'
      && (
        (petsAllowed.enabled && petsAllowed.value)
        || (catsAllowed.enabled && catsAllowed.value)
        || (dogsAllowed.enabled && dogsAllowed.value)
      )
    ) {
      inputValidations.push('required');
      inputValidations.push('positiveNumberString');

      if (formFieldEnabled) {
        checkboxProperties.disabled = true;
      }
    }

    return [
      ...memo,
      ...[
        {
          id: `${listingFormField.id}.enabled`,
          type: 'checkbox',
          properties: checkboxProperties,
        },
        {
          id: `${listingFormField.id}.id`,
          type: 'hidden',
        },
        {
          ...listingFormField,
          id: `${listingFormField.id}.value`,
          validations: formFieldEnabled
            ? [
              ...inputValidations,
              'required',
            ]
            : inputValidations,
          properties: {
            ...listingFormField.properties,
            disabled: listingFormField.properties.disabled || !formFieldEnabled,
          },
        },
        {
          id: `${listingFormField.id}.cascadingBehavior`,
          type: 'select',
          validations: formFieldEnabled
            ? ['required']
            : [],
          properties: {
            choices: PROPERTY_ATTRIBUTE_CASCADING_BEHAVIOR_OPTION_ITEMS,
            enableResetOption: true,
            placeholder: 'Behavior',
            disabled: !formFieldEnabled,
          },
        },
      ],
    ];
  }, []);
}

@withApiState({
  propertyAttributeUpsertListApiState: {
    action: SR_API_ACTION_PROPERTY_ATTRIBUTE_UPSERT_LIST,
  },
  propertyAttributeDeleteListApiState: {
    action: SR_API_ACTION_PROPERTY_ATTRIBUTE_DELETE_LIST,
  },
})
class SrPropertyAttributeListingFieldListForm extends Component {
  static propTypes = {
    title: PropTypes.string.isRequired,
    formType: PropTypes.string.isRequired,
    propertyAttributes: PropTypes.array.isRequired,
    apartmentId: PropTypes.number.isRequired,
    apartmentBuildingId: PropTypes.number,
    floorPlanId: PropTypes.number,
    onChange: PropTypes.func.isRequired,
    dispatch: PropTypes.func.isRequired,
    propertyAttributeUpsertListApiState: PropTypes.object.isRequired,
    propertyAttributeDeleteListApiState: PropTypes.object.isRequired,
  }

  _onValidateForm = () => {
    const { formType } = this.props;
    const formikBag = this.formik.getFormikBag();
    const formValues = formikBag.values;
    const formFields = getFormFields({ formType, formValues: formValues });

    const formErrors = createFormValidationFn(formFields)(formValues);
    const formFielIdsWithErrors = Object.keys(formErrors);

    if (!formFielIdsWithErrors.length) {
      formikBag.handleSubmit();
    }
    else {
      formFielIdsWithErrors.forEach((formFieldId) => {
        formikBag.setFieldTouched(formFieldId, true, true);
      });
      scrollToFormField({
        formFieldId: formFielIdsWithErrors[0],
      });
    }
  }

  _onSubmitForm = (formValues) => {
    const {
      propertyAttributes,
      apartmentId,
      apartmentBuildingId,
      floorPlanId, onChange,
      dispatch,
    } = this.props;
    const propertyAttributesByName = lodashKeyBy('name', propertyAttributes);
    const { propertyAttributesToUpsert, propertyAttributesToDelete } = Object.keys(formValues)
      .filter((listingFieldId) => isBoolean(formValues[listingFieldId].enabled))
      .reduce((memo, listingFieldId) => {
        const propertyAttribute = propertyAttributesByName[listingFieldId];

        if (formValues[listingFieldId].enabled && !propertyAttribute) {
          memo.propertyAttributesToUpsert.push({
            ...setNullEmptyValues({
              apartmentId: apartmentId,
              apartmentBuildingId: apartmentBuildingId,
              floorPlanId: floorPlanId,
              category: PROPERTY_ATTRIBUTE_CATEGORY_LISTING_FIELD,
              cascadingBehavior: formValues[listingFieldId].cascadingBehavior,
              name: listingFieldId,
              attrs: {
                value: formValues[listingFieldId].value,
              },
            }),
          });
        }
        else if (formValues[listingFieldId].enabled && (
          formValues[listingFieldId].cascadingBehavior !== propertyAttribute.cascadingBehavior
          || formValues[listingFieldId].value !== propertyAttribute.attrs.value)
        ) {
          memo.propertyAttributesToUpsert.push({
            ...propertyAttribute,
            cascadingBehavior: formValues[listingFieldId].cascadingBehavior,
            attrs: {
              value: formValues[listingFieldId].value,
            },
          });
        }
        else if (!formValues[listingFieldId].enabled && formValues[listingFieldId].id) {
          memo.propertyAttributesToDelete.push(formValues[listingFieldId].id);
        }
        return memo;
      }, { propertyAttributesToUpsert: [], propertyAttributesToDelete: [] });

    const apiCalls = [];
    if (propertyAttributesToUpsert.length) {
      apiCalls.push(
        dispatch(postWithState, {
          action: SR_API_ACTION_PROPERTY_ATTRIBUTE_UPSERT_LIST,
          payload: {
            propertyAttributes: propertyAttributesToUpsert,
          },
        }),
      );
    }
    if (propertyAttributesToDelete.length) {
      apiCalls.push(
        dispatch(postWithState, {
          action: SR_API_ACTION_PROPERTY_ATTRIBUTE_DELETE_LIST,
          payload: {
            propertyAttributeIds: propertyAttributesToDelete,
          },
        }),
      );
    }

    Promise.all(apiCalls).then(onChange);
  }

  render() {
    const {
      title,
      formType,
      propertyAttributes,
      propertyAttributeUpsertListApiState,
      propertyAttributeDeleteListApiState,
    } = this.props;
    const listingFieldgroup = listingFormTypeToFieldgroupMapping[formType];
    const defaultFormValues = propertyAttributes.reduce((memo, propertyAttributes) => ({
      ...memo,
      [propertyAttributes.name]: {
        enabled: !!propertyAttributes.id,
        id: propertyAttributes.id,
        value: propertyAttributes.attrs.value,
        cascadingBehavior: propertyAttributes.cascadingBehavior,
      },
    }), {});
    const inProgress = propertyAttributeUpsertListApiState.inProgress
      || propertyAttributeDeleteListApiState.inProgress;

    return (
      <Formik
        ref={(formik) => {
          this.formik = formik;
        }}
        initialValues={createInitialValues({
          formFields: getFormFields({ formType, formValues: defaultFormValues }),
          defaultValues: defaultFormValues,
        })}
        validate={(formValues) => {
          const formFields = getFormFields({ formType, formValues: formValues });
          return createFormValidationFn(formFields)(formValues);
        }}
        onSubmit={this._onSubmitForm}
        render={(formikBag) => {
          const formFields = getFormFields({ formType, formValues: formikBag.values });
          const formFieldsById = lodashKeyBy('id', formFields);
          const formFieldsDOM = listingFieldgroup.map((listingFieldId) => (
            <SrFlex key={listingFieldId}>
              <SrBox className='checkmark'>
                <SrFormikField
                  formField={formFieldsById[`${listingFieldId}.enabled`]}
                  formikBag={formikBag}
                  className={SR_FIELD_THEME_CHECKBOX_BLUE}
                />
              </SrBox>
              <SrFormikField
                formField={formFieldsById[`${listingFieldId}.value`]}
                formikBag={formikBag}
                className={SR_FIELD_THEME_ADMIN_HORIZONTAL_SMALL}
              />
              <SrBox className='cascadingBehavior'>
                <SrFormikField
                  formField={formFieldsById[`${listingFieldId}.cascadingBehavior`]}
                  formikBag={formikBag}
                  className={SR_FIELD_THEME_ADMIN_HORIZONTAL_SMALL}
                />
              </SrBox>
            </SrFlex>
          ));

          return (
            <Form className='SrPropertyAttributeListingFieldListForm'>
              <div className='titleContainer'>
                <SrText display='block' align='center' weight='bold'>{title}</SrText>
              </div>
              <div className='formFieldsContainer'>
                <SrFlex className='formFields'>
                  {formFieldsDOM}
                </SrFlex>
              </div>
              <div className='submitButtonContainer'>
                <ButtonDeprecated
                  onClick={this._onValidateForm}
                  disabled={inProgress}
                >
                  {inProgress ? 'Saving...' : 'Save'}
                </ButtonDeprecated>
              </div>
            </Form>
          );
        }}
      />
    );
  }
}

SrPropertyAttributeListingFieldListForm.displayName = 'SrPropertyAttributeListingFieldListForm';

export default SrPropertyAttributeListingFieldListForm;
